/**
 * Shared External-Agent-Framework catalog — the single source of truth for the
 * framework/agent picker, reused by both surfaces so they never drift:
 *   • /gel-connectors "Add External Agent Framework" (GelPlatformConnectorsPageV2)
 *   • the Register-ExteLet wizard "Bind framework" step (ExteletRegistrationWizard)
 *
 * FRAMEWORK_TYPES  — the 15 backend platform_type values (flat), for facet filters
 *                    and label lookups. Keep aligned with PlatformConnectorEntity /
 *                    the V366 CHECK (backend 400s on an unknown value).
 * FRAMEWORK_PICKER_GROUPS / FRAMEWORK_CHOICES / FRAMEWORK_TABS — the tabbed picker
 *                    rows. Coding agents + Copilot modes are listed INDIVIDUALLY,
 *                    each mapping to a platformType and (on create) an authConfig
 *                    `preset` (codingAgentVendor / invocationMode).
 */

export const FRAMEWORK_TYPES = [
  { key: 'OPENAI',            label: 'OpenAI' },
  { key: 'ANTHROPIC',         label: 'Anthropic' },
  { key: 'VERTEX_AI',         label: 'Google Vertex AI' },
  { key: 'AZURE_OPENAI',      label: 'Microsoft Azure OpenAI' },
  { key: 'MICROSOFT_COPILOT', label: 'Microsoft Copilot' },
  { key: 'CODING_AGENT',      label: 'Coding Agent' },
  { key: 'AGENTFORCE',        label: 'Salesforce AgentForce' },
  { key: 'SERVICENOW',        label: 'ServiceNow' },
  { key: 'UIPATH',            label: 'UiPath' },
  { key: 'LANGCHAIN',         label: 'LangChain' },
  { key: 'LANGGRAPH',         label: 'LangGraph' },
  { key: 'CREWAI',            label: 'CrewAI' },
  { key: 'AUTOGEN',           label: 'Microsoft AutoGen' },
  { key: 'CUSTOM_REST',       label: 'Custom REST API' },
  { key: 'CUSTOM_GRPC',       label: 'Custom gRPC' },
  { key: 'CUSTOM_MCP',        label: 'Custom MCP server' },
];

export const FRAMEWORK_PICKER_GROUPS = [
  { id: 'llm', label: 'LLM providers', items: [
    { id: 'OPENAI',       label: 'OpenAI',                 desc: 'GPT-4 / GPT-3.5 family',            platformType: 'OPENAI' },
    { id: 'ANTHROPIC',    label: 'Anthropic',              desc: 'Claude family',                    platformType: 'ANTHROPIC' },
    { id: 'VERTEX_AI',    label: 'Google Vertex AI',       desc: 'Gemini / PaLM',                    platformType: 'VERTEX_AI' },
    { id: 'AZURE_OPENAI', label: 'Microsoft Azure OpenAI', desc: 'OpenAI models hosted on Azure',    platformType: 'AZURE_OPENAI' },
  ]},
  { id: 'copilot', label: 'Microsoft Copilot', items: [
    { id: 'COPILOT_STUDIO', label: 'Copilot Studio agent',   desc: 'Published agent · native MCP tool / Direct Line', platformType: 'MICROSOFT_COPILOT', preset: { invocationMode: 'COPILOT_STUDIO' } },
    { id: 'M365_COPILOT',   label: 'Microsoft 365 Copilot',  desc: 'Declarative agent / Graph action',                platformType: 'MICROSOFT_COPILOT', preset: { invocationMode: 'M365_GRAPH' } },
    { id: 'AZURE_FOUNDRY',  label: 'Azure AI Foundry agent', desc: 'Azure AI Agent Service endpoint',                 platformType: 'MICROSOFT_COPILOT', preset: { invocationMode: 'AZURE_FOUNDRY' } },
  ]},
  { id: 'coding', label: 'Coding agents', items: [
    { id: 'CA_GITHUB_COPILOT', label: 'GitHub Copilot coding agent', desc: 'Issue → PR (GitHub Actions)',      platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'GITHUB_COPILOT' } },
    { id: 'CA_OPENAI_CODEX',   label: 'OpenAI Codex',                desc: 'Cloud coding agent',               platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'OPENAI_CODEX' } },
    { id: 'CA_CLAUDE_CODE',    label: 'Claude Code',                 desc: 'Terminal / CI coding agent',       platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'CLAUDE_CODE' } },
    { id: 'CA_COWORK',         label: 'Claude Cowork',               desc: 'Desktop agent — govern every chat', platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'COWORK' } },
    { id: 'CA_CURSOR',         label: 'Cursor',                      desc: 'Background agents',                platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'CURSOR' } },
    { id: 'CA_DEVIN',          label: 'Devin (Cognition)',           desc: 'Autonomous SWE, cloud sandbox',    platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'DEVIN' } },
    { id: 'CA_GOOGLE_JULES',   label: 'Google Jules',                desc: 'Async coding agent',               platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'GOOGLE_JULES' } },
    { id: 'CA_Q_DEVELOPER',    label: 'Amazon Q Developer',          desc: 'AWS coding agent',                 platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'Q_DEVELOPER' } },
    { id: 'CA_WINDSURF',       label: 'Windsurf',                    desc: 'Agentic IDE',                      platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'WINDSURF' } },
    { id: 'CA_REPLIT',         label: 'Replit Agent',                desc: 'Build / PR agent',                 platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'REPLIT' } },
    { id: 'CA_CLINE',          label: 'Cline',                       desc: 'CI / agent sessions',              platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'CLINE' } },
    { id: 'CA_AMP',            label: 'Sourcegraph Amp',             desc: 'Agent task → PR',                  platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'AMP' } },
    { id: 'CA_CUSTOM',         label: 'Other / custom coding agent', desc: 'Generic dispatch endpoint',        platformType: 'CODING_AGENT', preset: { codingAgentVendor: 'CUSTOM' } },
  ]},
  { id: 'enterprise', label: 'Enterprise & RPA', items: [
    { id: 'AGENTFORCE', label: 'Salesforce AgentForce', desc: 'CRM-native agents',       platformType: 'AGENTFORCE' },
    { id: 'SERVICENOW', label: 'ServiceNow',            desc: 'ITSM agents',             platformType: 'SERVICENOW' },
    { id: 'UIPATH',     label: 'UiPath',                desc: 'RPA / automation agents', platformType: 'UIPATH' },
  ]},
  // Coded-SDK frameworks: the agent runs in the customer's process and is governed in-process
  // via the Agentlets SDK (CODED path, agt_sk_), calling the gateway. No inbound endpoint, no
  // connector-held secret — see the `coded` flag + CODED_FRAMEWORK_TYPES below, which drive the
  // coded connector form. Served by the shared SdkFrameworkPlatformConnector on the backend.
  { id: 'frameworks', label: 'Frameworks', items: [
    { id: 'LANGCHAIN', label: 'LangChain',         desc: 'Python / JS LangChain agents',    platformType: 'LANGCHAIN', coded: true },
    { id: 'LANGGRAPH', label: 'LangGraph',         desc: 'Stateful graph agents (LangChain)', platformType: 'LANGGRAPH', coded: true },
    { id: 'CREWAI',    label: 'CrewAI',            desc: 'Multi-agent orchestration',       platformType: 'CREWAI', coded: true },
    { id: 'AUTOGEN',   label: 'Microsoft AutoGen', desc: 'Conversation-based framework',    platformType: 'AUTOGEN', coded: true },
  ]},
  { id: 'custom', label: 'Custom', items: [
    { id: 'CUSTOM_REST', label: 'Custom REST API',   desc: 'Any HTTP endpoint',            platformType: 'CUSTOM_REST' },
    { id: 'CUSTOM_GRPC', label: 'Custom gRPC',       desc: 'gRPC endpoint',                platformType: 'CUSTOM_GRPC' },
    { id: 'CUSTOM_MCP',  label: 'Custom MCP server', desc: 'Model Context Protocol server', platformType: 'CUSTOM_MCP' },
  ]},
];

export const FRAMEWORK_CHOICES = FRAMEWORK_PICKER_GROUPS.flatMap(
  (g) => g.items.map((it) => ({ ...it, group: g.id })));

export const FRAMEWORK_TABS = FRAMEWORK_PICKER_GROUPS.map((g) => ({ id: g.id, label: g.label }));

export const choiceById = (id) => FRAMEWORK_CHOICES.find((c) => c.id === id) || null;

/** First picker row for a backend platformType — used to reflect a stored/seeded
 *  platformType back into the picker's selection. */
export const firstChoiceForType = (platformType) =>
  FRAMEWORK_CHOICES.find((c) => c.platformType === platformType) || null;

/** Friendly label for a backend platformType value. */
export const typeLabel = (platformType) =>
  (FRAMEWORK_TYPES.find((t) => t.key === platformType)?.label) || platformType;

/**
 * Coded-SDK framework platform_types — the agent runs in the customer's process and is
 * governed in-process via the SDK (no inbound endpoint, no connector-held secret). Single
 * FE source of truth (mirrors PlatformConnectorEntity.CODED_SDK_FRAMEWORKS on the backend);
 * drives the coded connector form in GelPlatformConnectorsPageV2. Derived from the catalog's
 * `coded` flag so it can never drift from the picker rows.
 */
export const CODED_FRAMEWORK_TYPES = new Set(
  FRAMEWORK_CHOICES.filter((c) => c.coded).map((c) => c.platformType));

/** True when a backend platformType is a coded-SDK framework (null-safe). */
export const isCodedFramework = (platformType) =>
  !!platformType && CODED_FRAMEWORK_TYPES.has(platformType);
